import React, { useMemo } from "react";
import { ActionIcon, Box, Flex, Text } from "@mantine/core";
import { IconX } from "@tabler/icons";

import { ReleaseOption } from "../../utils/releaseOptions";

interface SelectedReleasesListProps {
  /** Ids exactly as stored on the target, in stored order. */
  releaseIds: string[];
  /**
   * The **full** option list, not the tree's — the whole point of this section
   * is to name releases the tree no longer renders.
   */
  options: ReleaseOption[];
  /** Called with the id to drop; the caller writes the remaining set. */
  onUnselect: (releaseId: string) => void;
  readOnly?: boolean;
}

const ROW_SX = {
  borderRadius: 4,
  "&:hover": { backgroundColor: "#F8F8FB" },
};

/**
 * The idea's current releases, pinned above the group tree in
 * `ReleaseAssignmentMenu`.
 *
 * No checkbox: every row here is selected by definition, so the only action is
 * removal. A release tagged with two groups is one row naming both, which is
 * the thing the tree could not do — it ticked the same release twice.
 */
export const SelectedReleasesList: React.FC<SelectedReleasesListProps> = ({
  releaseIds,
  options,
  onUnselect,
  readOnly = false,
}) => {
  const rows = useMemo(() => {
    const byId = new Map<string, { label: string; groups: string[] }>();
    options.forEach((option) => {
      const key = option.value.toLowerCase();
      const existing = byId.get(key);
      if (existing) {
        if (!existing.groups.includes(option.releaseGroupName)) {
          existing.groups.push(option.releaseGroupName);
        }
        return;
      }
      byId.set(key, {
        label: option.label,
        groups: option.releaseGroupName ? [option.releaseGroupName] : [],
      });
    });

    return (releaseIds || []).map((id) => {
      const resolved = byId.get(String(id).toLowerCase());
      return {
        id: String(id),
        // An id past the picker's page cap has no option and is still a real
        // assignment, so it is named by its id rather than hidden.
        label: resolved?.label ?? String(id),
        group: (resolved?.groups || []).join(", "),
      };
    });
  }, [releaseIds, options]);

  if (rows.length === 0) return null;

  return (
    <Box data-testid="release-picker-selected" px={5} pt={3} pb={5}>
      <Text size="xs" color="dimmed" px="sm" py={3}>
        Selected
      </Text>
      {rows.map((row) => (
        <Flex
          key={row.id}
          align="center"
          gap={6}
          px="sm"
          py={3}
          my={2}
          sx={ROW_SX}
          data-testid="release-picker-selected-row"
          data-release-id={row.id}
        >
          <Text size="sm" lineClamp={1}>
            {row.label}
          </Text>
          {row.group && (
            <Text size="xs" color="dimmed" lineClamp={1}>
              {row.group}
            </Text>
          )}
          {!readOnly && (
            <ActionIcon
              size="xs"
              variant="subtle"
              ml="auto"
              aria-label={`Unselect ${row.label}`}
              data-testid="release-picker-unselect"
              onClick={() => onUnselect(row.id)}
            >
              <IconX size={13} color="#585C68" />
            </ActionIcon>
          )}
        </Flex>
      ))}
    </Box>
  );
};

export default SelectedReleasesList;
